import { formatInTimeZone } from 'date-fns-tz';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { MdAddCircle, MdRemoveCircleOutline } from 'react-icons/md';
import { Checkbox, IconButton, TimeInput } from '@ds';
import { ShiftUnavailabilitiesFormType } from '@lp/features/Scheduler/SchedulerSurgeons/components/NAShiftModal/types';
import {
  convertAbsoluteDateTimeToZoned,
  convertTimeToAbsoluteDateTime,
} from '@lp/features/Scheduler/SchedulerSurgeons/components/NAShiftModal/utils';

type NAShiftModalFormProps = {
  date: string;
  timezone: string;
};

export const NAShiftModalForm = ({ date, timezone }: NAShiftModalFormProps) => {
  const { control } = useFormContext<ShiftUnavailabilitiesFormType>();
  const [fullDay] = useWatch({ control, name: ['fullDay'] });
  const { fields, append, remove } = useFieldArray<ShiftUnavailabilitiesFormType>({
    control,
    name: 'unavailabilities',
  });
  const tzAbbr = formatInTimeZone(new Date(date), timezone, 'zzz');

  const addNewRow = () => {
    append({ from: '', to: '' });
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2 items-center justify-between">
        <span className="text-l-sb text-base-5">Time Slots ({tzAbbr})</span>
        <Controller
          name="fullDay"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Checkbox isSelected={value} onValueChange={onChange} {...field}>
              All Day
            </Checkbox>
          )}
        />
      </div>
      <div className="flex flex-col gap-3">
        {fields?.map((u, index) => {
          return (
            <div key={u.id} className="flex items-center gap-2">
              <Controller
                name={`unavailabilities.${index}.from`}
                control={control}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                  <TimeInput
                    hourCycle={24}
                    isDisabled={fullDay}
                    aria-label="from"
                    className="max-w-[120px]"
                    value={convertAbsoluteDateTimeToZoned(value, timezone)}
                    onChange={(zdt) => onChange(convertTimeToAbsoluteDateTime(zdt, timezone, date))}
                    isInvalid={invalid}
                    {...field}
                  />
                )}
              />
              <span>to</span>
              <Controller
                name={`unavailabilities.${index}.to`}
                control={control}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                  <TimeInput
                    hourCycle={24}
                    isDisabled={fullDay}
                    aria-label="to"
                    className="max-w-[120px]"
                    value={convertAbsoluteDateTimeToZoned(value, timezone)}
                    onChange={(zdt) => onChange(convertTimeToAbsoluteDateTime(zdt, timezone, date))}
                    isInvalid={invalid}
                    {...field}
                  />
                )}
              />
              {index === 0 ? (
                <IconButton
                  size="sm"
                  variant="light"
                  isDisabled={fullDay}
                  startContent={<MdAddCircle />}
                  onClick={addNewRow}
                />
              ) : (
                <IconButton
                  size="sm"
                  variant="light"
                  isDisabled={fullDay}
                  startContent={<MdRemoveCircleOutline />}
                  onClick={() => remove(index)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
